import {
  Button,
  Flexbox,
  Icons,
  Label,
  Sizes,
  Text,
  Tooltip
} from "@sede-x/shell-ds-react-framework";
import { format } from "date-fns";
import { usePicklists } from "ticketing/contexts/picklists/PicklistContextProvider";
import { TMovementGroup, TTicket, TValidatedTicket, TVolume } from "ticketing/ticketing.types";
import { formatNumber } from "ticketing/utils";
import { findMatchingVolume } from "./volume_util";

export const AvailableTicket = ({
  activeMovementGroup,
  ticket,
  disableLinks,
  onShowTicketRevisions,
  onLinkTicket,
  onEditTicket,
  showDocument
}: {
  activeMovementGroup?: TMovementGroup | null;
  ticket: TValidatedTicket;
  disableLinks: boolean;
  onShowTicketRevisions: (ticketId: string) => void;
  onLinkTicket: (ticket: TValidatedTicket) => void;
  onEditTicket: (ticket: TTicket) => void;
  showDocument: (documentId: string) => void;
}) => {
  const { picklists } = usePicklists();
  const hasErrors = (ticket.validationResults?.filter(r => !!r.error).length ?? 0) > 0;
  const hasWarningsOrErrors =
    (ticket.validationResults?.filter(r => r.warning ?? r.error).length ?? 0) > 0;
  const matchingVolume: TVolume = findMatchingVolume(
    ticket,
    activeMovementGroup,
    picklists?.uomConversions
  );

  return (
    <div key={`${ticket.id}`} style={{ padding: "4px" }}>
      <div className="ticket-component" id="target">
        <div className="ticket-component-outter">
          <Flexbox flexWrap="wrap" gap="1px">
            <Flexbox flexWrap="nowrap" gap="1px">
              <Text
                style={{ color: "#09B0EC", fontSize: "12px" }}>{`${ticket.ticketNumber}`}</Text>
              {!!ticket.documentId && (
                <Button
                  icon={<Icons.FilePdfOutline style={{ fill: "limegreen" }} />}
                  onClick={() => showDocument(ticket.documentId!)}
                  title={ticket.document?.fileName ?? "Ticket has document attached"}
                  className="document-icon-button"
                />
              )}
            </Flexbox>
            <Text style={{ fontSize: "12px" }}>
              {ticket?.startDate
                ? `(${format(new Date(ticket.startDate), "yyyy-MM-dd")})`
                : "(No date)"}
            </Text>
          </Flexbox>
          <Flexbox gap="2px">
            <Button
              title="Edit Ticket"
              icon={<Icons.EditOutlined style={{ fill: "#ff6358" }} />}
              onClick={() => onEditTicket(ticket)}
              className="linked-ticket-button"
            />
            <Button
              title="Ticket Revisions"
              icon={<Icons.Time style={{ fill: "var(--primary-icon-color)" }} />}
              onClick={() => onShowTicketRevisions(ticket.id)}
              className="linked-ticket-button"
            />
            {hasErrors || (
              <Button
                disabled={disableLinks}
                icon={<Icons.Link style={{ fill: "green" }} />}
                className="linked-ticket-button"
                onClick={() => onLinkTicket(ticket)}
                title="Link Ticket"></Button>
            )}

            {hasWarningsOrErrors && (
              <Tooltip
                overlay={
                  <div
                    className="validation-tooltip"
                    style={{
                      maxWidth: "250px",
                      padding: "10px",
                      backgroundColor: "#ffffe0",
                      borderRadius: "8px"
                    }}>
                    <ul style={{ margin: 0, paddingLeft: "10px", listStyleType: "circle" }}>
                      {ticket?.validationResults
                        ?.sort((r1, r2) => (r2.error ? 1 : 0) - (r1.error ? 1 : 0))
                        ?.map(r => {
                          return (
                            <li key={r.message}>
                              <span style={{ color: `${r.error ? "red" : "black"}` }}>
                                {r.message}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                }
                trigger={
                  <Button
                    size={Sizes.Small}
                    icon={<Icons.Alert style={{ fill: "rgb(255, 198, 0)" }} />}
                    className="validation-warning-button"
                  />
                }
                placement="left"
              />
            )}
          </Flexbox>
        </div>

        <div className="ticket-component-label-wrapper">
          <Label size="small">{`Net:${formatNumber(matchingVolume?.netVolume ?? 0.0)}`}</Label>
          <Label size="small">{`Gross:${formatNumber(
            matchingVolume?.grossVolume ?? 0.0
          )}`}</Label>
          <Label size="small">{matchingVolume?.unitOfMeasure?.name}</Label>
          <Label size="small">{`${
            matchingVolume?.temperature
          }${matchingVolume?.temperatureUnitOfMeasure?.charAt(0)}`}</Label>
        </div>
      </div>
    </div>
  );
};
